// The Web Audio half of a voice session: one context serving both directions.
//
// Nothing here decides anything — voiceSession.js owns the rules. This is the adapter that gives
// it a microphone and a pair of speakers, and it is the only file in the pair that cannot be
// tested, since jsdom has neither AudioWorklet nor getUserMedia. The one piece of arithmetic with
// a consequence — how much of an answer was heard — is exported on its own so it can be.

// The rate the Realtime API speaks, in both directions. Asking the context for it directly leaves
// the browser to resample the microphone, which it does better than we would.
const SAMPLE_RATE = 24000;

/**
 * How many milliseconds of the answer had been heard by [now], from the chunks booked to play it.
 *
 * Not the time since playback began: a stall leaves a silent gap in the schedule, and time passes
 * across it while audio does not. Counting chunk by chunk stops at the last one that started, so a
 * gap contributes nothing — which matters because this number is what the model is told the student
 * heard, and anything too generous leaves it holding words nobody did.
 */
export function playedMsAt(chunks, now) {
  let played = 0;
  for (const chunk of chunks) {
    if (now < chunk.startsAt) {
      break;
    }
    played = chunk.offsetMs + Math.min((now - chunk.startsAt) * 1000, chunk.ms);
  }
  return played;
}

/**
 * Whether any of [chunks] still has speech to come at [now] — whether, in other words, there is
 * anything here for an interruption to interrupt.
 *
 * A press that lands after the last word has already been spoken cuts nothing off, and an answer
 * that was heard to its end must not then be trimmed: the model would lose the tail of what it
 * finished saying, and go on from a sentence the student did hear.
 */
export function isPendingAt(chunks, now) {
  return chunks.some((chunk) => chunk.startsAt + chunk.ms / 1000 > now);
}

function toBase64(buffer) {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.length; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

function fromBase64(base64) {
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes.buffer;
}

export function createVoiceAudio() {
  let context = null;
  let stream = null;
  let capture = null;
  let source = null;
  let capturing = false;
  // When the next chunk of the answer should start. Playing each one "now" would overlap them into
  // noise; each is booked to begin where the last one ends.
  let playHead = 0;
  // Every chunk booked but not yet finished, plus those of the answer being spoken now. They are
  // the only handle on sound that has been scheduled and not yet made, and the ones already spoken
  // still carry the offsets the rest of their answer is measured from.
  let chunks = [];
  let answerMs = 0;
  // Which answer the offsets are being counted from. A number rather than an item id: which chunk
  // belongs to which answer is voiceSession's to know, and it says so by calling startAnswer.
  let answer = 0;

  // Both directions, or only the speakers when [onFrame] is null: a session nobody speaks into
  // never asks for the device, so the browser raises no permission prompt for one.
  async function open(onFrame) {
    // Anything already built is given back if a later step fails. Half of this is a live
    // microphone: leaving it recording, with the tab's indicator lit, is not an option, and the
    // contexts of repeated failed attempts pile up until the browser refuses to make another.
    try {
      // The microphone stays open for the whole session, so the model can be interrupted — and so
      // echo cancellation is still running while it speaks into the room the microphone is in.
      if (onFrame) {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: { echoCancellation: true, noiseSuppression: true, channelCount: 1 },
        });
      }

      context = new AudioContext({ sampleRate: SAMPLE_RATE });
      // The click that began the session was consumed by the awaits above, so the context can
      // arrive suspended — in which case nothing is heard and nothing is captured, silently.
      if (context.state === 'suspended') {
        await context.resume();
      }

      // Playback needs none of what follows: it books its buffers straight onto the destination.
      if (!onFrame) {
        return;
      }

      await context.audioWorklet.addModule(`${process.env.PUBLIC_URL}/pcm-worklet.js`);
      source = context.createMediaStreamSource(stream);
      capture = new AudioWorkletNode(context, 'pcm-capture');
      capture.port.onmessage = ({ data }) => {
        if (capturing) {
          onFrame(toBase64(data));
        }
      };
      source.connect(capture);

      // A worklet that reaches no destination is not guaranteed to be pulled. A silent sink keeps
      // it running without putting the microphone through the speakers.
      const silence = context.createGain();
      silence.gain.value = 0;
      capture.connect(silence).connect(context.destination);
    } catch (error) {
      close();
      throw error;
    }
  }

  function setCapturing(on) {
    capturing = on;
  }

  /**
   * Begin counting a new answer, the previous one having been spoken to its end.
   *
   * Its chunks are let go of, except any still booked to play: those stay stoppable, or an answer
   * that runs on into the next one keeps speaking through an interruption that cannot reach it.
   */
  function startAnswer() {
    chunks = chunks.filter((chunk) => chunk.node);
    answerMs = 0;
    answer += 1;
  }

  /**
   * The chunks of the answer being spoken now. Earlier answers may still have entries here — one
   * running on into the next has to stay stoppable — and they are not what is being measured.
   */
  function currentChunks() {
    return chunks.filter((chunk) => chunk.answer === answer);
  }

  /**
   * How much of the answer being spoken has left the speakers, in milliseconds.
   *
   * Read on a timer while the answer plays: it is what paces the words on the screen against the
   * voice, so that reading it is not reading ahead of it.
   */
  function playedMs() {
    return context ? playedMsAt(currentChunks(), context.currentTime) : 0;
  }

  /**
   * Whether anything booked is still to be heard — whether the AI is, right now, talking.
   *
   * Every answer, not only the one being measured: one running on into the next is still coming out
   * of the speakers, and a screen waiting for the room to go quiet is waiting for that too.
   */
  function speaking() {
    return context ? isPendingAt(chunks, context.currentTime) : false;
  }

  /** Books [base64] to play after everything already booked, and reports where it ends. */
  function play(base64) {
    const pcm = new Int16Array(fromBase64(base64));
    const buffer = context.createBuffer(1, pcm.length, SAMPLE_RATE);
    const channel = buffer.getChannelData(0);
    for (let i = 0; i < pcm.length; i += 1) {
      channel[i] = pcm[i] / 0x8000;
    }

    const chunk = context.createBufferSource();
    chunk.buffer = buffer;
    chunk.connect(context.destination);
    // Behind the clock after a gap in the answer: resume from now rather than from the past.
    playHead = Math.max(playHead, context.currentTime);
    chunk.start(playHead);
    const booked = {
      node: chunk,
      answer,
      startsAt: playHead,
      offsetMs: answerMs,
      ms: buffer.duration * 1000,
    };
    // Once it has been spoken there is nothing left to stop, and holding the node holds its decoded
    // audio with it — a minute of answer is several megabytes that cannot be collected. The timings
    // stay: they are what the rest of the answer is measured against.
    chunk.onended = () => {
      booked.node = null;
    };
    chunks.push(booked);
    playHead += buffer.duration;
    answerMs += buffer.duration * 1000;
    // Where this chunk ends, so the caller can date the words that arrived with it.
    return answerMs;
  }

  /**
   * Drop the rest of the answer, reporting how much of it was heard first.
   *
   * The provider generates far faster than it speaks, so an answer is typically complete — and
   * often already reported done — while seconds of it are still booked to play. Nothing said
   * upstream can reclaim those: they exist only here.
   */
  function flush() {
    const now = context ? context.currentTime : 0;
    // Measured against the answer being spoken now, which is the one an interruption cuts short and
    // the only one the session will name. Anything left over from an earlier answer is still
    // stopped — it is audible, and the student asked for silence.
    const current = currentChunks();
    const heardMs = playedMsAt(current, now);
    const cut = isPendingAt(current, now);

    chunks.forEach((chunk) => chunk.node?.stop());
    chunks = [];
    answerMs = 0;
    playHead = 0;
    return { heardMs, cut };
  }

  function close() {
    capturing = false;
    stream?.getTracks().forEach((track) => track.stop());
    capture?.disconnect();
    source?.disconnect();
    context?.close();
    context = null;
    stream = null;
    capture = null;
    source = null;
    playHead = 0;
    chunks = [];
    answerMs = 0;
  }

  return { open, setCapturing, startAnswer, play, playedMs, speaking, flush, close };
}
